import * as R from 'ramda'
import {
  ClientFrontlineConfig,
  EquipmentBase,
  EquipmentGroups,
  FlexibleScope,
  InteractionScope,
  Offer,
  EquipmentGroup,
  Elevator,
  Door,
  Equipment,
  ResiFlowInteractionScope
} from '../../../../types/types'

import {
  AssessmentResult,
  ValidForQuickTender,
  ValidForFlexible,
  ValidForValueAddedServiceAddendum,
  AssessmentCriticality,
  Invalid,
  ValidForFlexibleDoors,
  OnlyDoorsAssessmentGroup,
  AssessmentGroup,
  ValidForGSMCampaign // TODO: Remove as part of GSM Clean up
} from './types'
// import { isEscalationWithVasAddendumOffer } from '../../components/EscalationOfferWizard/escalation-offer-utils'
import { buildingRatings, RatingsResult } from '../../utils/assessment'
import { AssessmentProgress, getAssessmentProgress } from '../assessment/assessment-progress'

function isValidForFlexible(offer: Offer, assessmentProgress: AssessmentProgress): boolean {
  return Boolean(
    assessmentProgress.isValid && (assessmentProgress.isOnlyPeopleflowOffer ? true : offer.selectedInteractionRating)
  )
}

function isValidForQuickTender(offer: Offer, assessmentProgress: AssessmentProgress) {
  return assessmentProgress.isValidForQuickTender
}

function isValidForValueAddedServiceAddendum(offer: Offer, assessmentProgress: AssessmentProgress) {
  return isValidForFlexible(offer, assessmentProgress)
}

export function toDomain(offer: Offer, frontlineConfig: ClientFrontlineConfig): AssessmentResult {
  const assessmentProgress = getAssessmentProgress(offer, frontlineConfig)
  const allEquipment = R.chain(building => building.details.equipment, offer.buildings)
  const { isOnlyPeopleflowOffer } = assessmentProgress
  if (isValidForValueAddedServiceAddendum(offer, assessmentProgress)) {
    const elevators: Elevator[] = R.chain(e => (e.equipmentCategory === 'elevator' ? [e] : []), allEquipment)
    const equipmentByGroup = R.groupBy(e => e.groupId, elevators)
    return !offer.isGSMOffer // TODO: Remove as part of GSM Clean up
      ? new ValidForValueAddedServiceAddendum(
          R.map(
            g => ({
              groupId: g.salesToolId,
              name: g.name,
              groupKind: 'elevators_and_escalators',
              elevators: equipmentByGroup[g.salesToolId]
            }),
            offer.equipmentGroups
          ),
          assessmentProgress.errors
        )
      : new ValidForGSMCampaign(
          R.map(
            g => ({
              groupId: g.salesToolId,
              name: g.name,
              groupKind: 'elevators_and_escalators',
              elevators: equipmentByGroup[g.salesToolId]
            }),
            offer.equipmentGroups
          ),
          assessmentProgress.errors
        )
  } else if (isValidForFlexible(offer, assessmentProgress)) {
    const recommendedOfferByGroup = buildingRatings(frontlineConfig, offer)
    return ratingsToValidAssessment(
      frontlineConfig.scopes,
      offer.selectedInteractionRating!,
      recommendedOfferByGroup,
      allEquipment,
      offer.equipmentGroups,
      isOnlyPeopleflowOffer
    )
  } else if (isValidForQuickTender(offer, assessmentProgress)) {
    const elevators: Elevator[] = R.chain(e => (e.equipmentCategory === 'elevator' ? [e] : []), allEquipment)
    const equipmentByGroup = R.groupBy(e => e.groupId, elevators)
    return new ValidForQuickTender(
      R.map(
        g => ({
          groupId: g.salesToolId,
          name: g.name,
          groupKind: 'elevators_and_escalators',
          elevators: equipmentByGroup[g.salesToolId]
        }),
        offer.equipmentGroups
      ),
      assessmentProgress.errors
    )
  } else {
    return new Invalid(assessmentProgress.errors)
  }
}

export function ratingsToValidAssessment(
  scopes: FlexibleScope[],
  interactionScope: InteractionScope | ResiFlowInteractionScope,
  recommendedOfferByGroup: RatingsResult,
  allEquipment: Equipment[],
  equipmentGroups: EquipmentGroups,
  isOnlyPeopleflowOffer: boolean
): AssessmentResult {
  function buildCategory(
    equipment: EquipmentBase[],
    rating: FlexibleScope,
    isResiflowGroup: boolean = false
  ): AssessmentCriticality {
    // if resiflow group ignore the recommended offering and set 'resiflow' as default.
    if (isResiflowGroup) {
      return {
        // all apf equipment are normal
        normal: equipment.length > 0 ? 'resiflow' : null,
        critical: null
      }
    }
    const normalEquipment = equipment.filter(e => e.criticality === 'normal')
    const ratings = ratingToScopes(scopes, rating)
    return {
      normal: normalEquipment.length > 0 ? ratings.normal : null,
      critical: null
    }
  }

  function makeDoorGroup(group: EquipmentGroup, doors: Door[]): OnlyDoorsAssessmentGroup {
    const categories = recommendedOfferByGroup[group.salesToolId]
    return {
      groupId: group.salesToolId,
      name: group.name,
      groupKind: 'doors',
      doors,
      criticalities: buildCategory(doors, categories.door.scope)
    }
  }

  function makeElevatorAndEscalatorGroup(
    group: EquipmentGroup,
    elevators: EquipmentBase[],
    escalators: EquipmentBase[],
    resiflow: EquipmentBase[]
  ): AssessmentGroup {
    const categories = recommendedOfferByGroup[group.salesToolId]
    const equipmentInGroup = equipmentByEquipmentGroup[group.salesToolId]
    return {
      groupId: group.salesToolId,
      name: group.name,
      groupKind: group.groupKind,
      equipment: equipmentInGroup,
      criticalities: {
        elevator: elevators.length > 0 ? buildCategory(elevators, categories.elevator.scope) : null,
        escalator: escalators.length > 0 ? buildCategory(escalators, categories.escalator.scope) : null,
        resiflow: resiflow.length > 0 ? buildCategory(resiflow, categories.resiflow.scope, true) : null
      }
    }
  }

  const equipmentByEquipmentGroup = R.groupBy(e => e.groupId, allEquipment)
  const hasDoors = allEquipment.filter(e => e.equipmentCategory === 'door').length > 0

  return hasDoors
    ? new ValidForFlexibleDoors(
        R.map(g => {
          const equipmentInGroup = equipmentByEquipmentGroup[g.salesToolId]
          const doors: Door[] = R.chain(e => (e.equipmentCategory === 'door' ? [e] : []), equipmentInGroup)
          return makeDoorGroup(g, doors)
        }, equipmentGroups),
        interactionScope as InteractionScope
      )
    : new ValidForFlexible(
        R.map(g => {
          const equipmentInGroup = equipmentByEquipmentGroup[g.salesToolId]
          const elevators = equipmentInGroup.filter(e => e.equipmentCategory === 'elevator')
          const escalators = equipmentInGroup.filter(e => e.equipmentCategory === 'escalator')
          const resiflow = equipmentInGroup.filter(e => e.equipmentCategory === 'resiflow')
          return makeElevatorAndEscalatorGroup(g, elevators, escalators, resiflow)
        }, equipmentGroups),
        isOnlyPeopleflowOffer ? (null as ResiFlowInteractionScope) : (interactionScope as InteractionScope)
      )
}

// For a recommended rating, give back the normal and critical scopes
function ratingToScopes(
  scopes: FlexibleScope[],
  rating: FlexibleScope
): { normal: FlexibleScope; critical: FlexibleScope } {
  const normalScopeIndex = scopes.indexOf(rating!)
  const criticalScopeIndex = normalScopeIndex > 0 ? normalScopeIndex - 1 : normalScopeIndex
  return {
    normal: scopes[normalScopeIndex],
    critical: scopes[criticalScopeIndex]
  }
}
